import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, ReactNode } from 'react';
import { db } from '../config/firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { useCustomers } from './CustomerContext';
import { useOrders } from './OrderContext';
import { useAuth } from './AuthContext';
import { COLLECTIONS } from '../config/constants';

export interface MarketingSettings {
  activeDays: number; // <= active
  slippingDays: number; // <= slipping
  churnRiskDays: number; // <= risk else lost
  includeNeverOrdered: boolean;
}

export type CustomerSegment = 'active' | 'slipping' | 'churnRisk' | 'lost' | 'neverOrdered';

export interface AddressStat {
  addressKey: string;
  neighborhood: string;
  street: string;
  buildingInfo: string;
  customers: string[]; // customer ids
  lastOrderDate?: string; // YYYY-MM-DD
  orderCount: number;
  totalAmount: number;
  segment: CustomerSegment;
  daysSinceLast?: number; // undefined if neverOrdered
}

export interface Campaign {
  id: string;
  title: string;
  description: string;
  discountType: 'percentage' | 'fixed' | 'buy_x_get_y';
  discountValue: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
  productIds?: string[]; // Kampanya hangi ürünlere uygulanacak (boşsa tüm ürünler)
  minOrderAmount?: number;
  buyQuantity?: number; // 10+1 kampanyası için (10 al)
  freeQuantity?: number; // 10+1 kampanyası için (1 bedava)
  applyToProducts?: boolean; // true ise sadece seçili ürünlere uygulanır
  createdAt: string;
}

export interface DiscountCode {
  id: string;
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
  minOrderAmount?: number;
  usageLimit?: number; // Toplam kullanım limiti
  usageCount: number; // Şimdiye kadar kaç kez kullanıldı
  perUserLimit?: number; // Kullanıcı başına limit
  createdAt: string;
}

interface MarketingContextType {
  settings: MarketingSettings;
  updateSettings: (p: Partial<MarketingSettings>) => void;
  addressStats: AddressStat[];
  passiveAddresses: AddressStat[]; // churnRisk + lost (and optional neverOrdered)
  newlyPassiveKeys: string[];
  clearNewlyPassive: () => void;
  // Campaign management
  campaigns: Campaign[];
  addCampaign: (campaign: Omit<Campaign, 'id' | 'createdAt'>) => void;
  updateCampaign: (id: string, updates: Partial<Campaign>) => void;
  deleteCampaign: (id: string) => void;
  // Discount code management
  discountCodes: DiscountCode[];
  addDiscountCode: (code: Omit<DiscountCode, 'id' | 'createdAt' | 'usageCount'>) => void;
  updateDiscountCode: (id: string, updates: Partial<DiscountCode>) => void;
  deleteDiscountCode: (id: string) => void;
  validateDiscountCode: (code: string, orderAmount: number, userId?: string) => { valid: boolean; discount?: number; message?: string };
  useDiscountCode: (code: string) => void;
}

const MarketingContext = createContext<MarketingContextType | undefined>(undefined);

export const useMarketing = () => {
  const ctx = useContext(MarketingContext);
  if(!ctx) throw new Error('useMarketing must be used within MarketingProvider');
  return ctx;
};

const LS_SETTINGS_KEY = 'marketingSettings';
const LS_PREV_PASSIVE = 'previousPassiveAddresses';
const LS_CAMPAIGNS_KEY = 'marketingCampaigns';
const LS_DISCOUNT_CODES_KEY = 'marketingDiscountCodes';

const defaultSettings: MarketingSettings = {
  activeDays: 7,
  slippingDays: 14,
  churnRiskDays: 30,
  includeNeverOrdered: true,
};

// Varsayılan kampanyalar
const defaultCampaigns: Campaign[] = [
  {
    id: 'default-10plus1',
    title: '10+1 Damacana Kampanyası',
    description: '10 damacana alana 1 damacana hediye! Damacana ürünlerinde geçerlidir.',
    discountType: 'buy_x_get_y',
    discountValue: 0,
    buyQuantity: 10,
    freeQuantity: 1,
    startDate: '2024-01-01',
    endDate: '2025-12-31',
    isActive: true,
    productIds: [], // Frontend'de seçilecek
    applyToProducts: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'default-gelal',
    title: 'Gel-Al Kampanyası',
    description: 'Mağazadan gel-al siparişlerinde %15 indirim! Minimum sipariş tutarı: 100₺',
    discountType: 'percentage',
    discountValue: 15,
    startDate: '2024-01-01',
    endDate: '2025-12-31',
    isActive: true,
    minOrderAmount: 100,
    applyToProducts: false, // Tüm ürünlere uygulanır
    createdAt: new Date().toISOString()
  }
];

export const MarketingProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const { customers } = useCustomers();
  const { orders } = useOrders();
  const { currentUser, loading: authLoading } = useAuth();
  const isAdmin = useMemo(() => (currentUser?.role || '').toLowerCase() === 'admin', [currentUser?.role]);

  const USE_FB_MARKETING = (process.env.REACT_APP_USE_FIREBASE_MARKETING || '').toLowerCase() === 'true';

  const [settings, setSettings] = useState<MarketingSettings>(() => {
    try {
      const raw = localStorage.getItem(LS_SETTINGS_KEY);
      if(raw) return { ...defaultSettings, ...JSON.parse(raw) } as MarketingSettings;
    } catch {}
    return defaultSettings;
  });

  const updateSettings = useCallback((p: Partial<MarketingSettings>) => {
    setSettings(prev => ({...prev, ...p}));
  }, []);

  // Persist settings (Firestore or localStorage)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{
    if (USE_FB_MARKETING) {
      if (authLoading || !currentUser || !isAdmin) return;
      setDoc(doc(db, COLLECTIONS.CONFIG, 'marketingSettings'), settings, { merge: true }).catch(()=>{});
    } else {
      localStorage.setItem(LS_SETTINGS_KEY, JSON.stringify(settings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, USE_FB_MARKETING, currentUser?.uid, authLoading, isAdmin]);

  // Subscribe to Firestore settings when enabled
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{
  if (!USE_FB_MARKETING) return;
  if (authLoading || !currentUser) return;
    const ref = doc(db, COLLECTIONS.CONFIG, 'marketingSettings');
    const unsub = onSnapshot(ref, (snap)=>{
      const data = snap.data();
      if (data) setSettings(prev => ({ ...prev, ...data }));
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [USE_FB_MARKETING, currentUser?.uid, authLoading]);

  // Build orders aggregated by customer/address
  const addressStats = useMemo<AddressStat[]>(() => {
    // addressKey creation helper
    const makeKey = (n: string, s: string, b: string) => [n,s,b].map(x=> (x||'').trim().toLowerCase()).join('|');
    interface Agg { lastOrderDate?: string; orderCount: number; totalAmount: number; customers: Set<string>; n: string; s: string; b: string; }
    const map = new Map<string, Agg>();

    orders.forEach(o => {
      // SADECE customerId olan siparişleri işle
      if (!o.customerId) return;
      
      // Müşteriyi bul
      const cust = customers.find(c=> c.id === o.customerId);
      if (!cust || !cust.address) return;
      
      // Müşterinin adres bilgisini kullan
      const neighborhood = cust.address.neighborhood || '';
      const street = cust.address.street || '';
      const buildingInfo = cust.address.buildingInfo || '';
      
      const key = makeKey(neighborhood, street, buildingInfo);
      const entry = map.get(key) || { lastOrderDate: undefined, orderCount:0, totalAmount:0, customers: new Set<string>(), n: neighborhood, s: street, b: buildingInfo };
      entry.orderCount += 1;
      entry.totalAmount += o.amount;
      // we only have deliveryDate (YYYY-MM-DD)
      if(!entry.lastOrderDate || o.deliveryDate > entry.lastOrderDate) entry.lastOrderDate = o.deliveryDate;
      entry.customers.add(o.customerId);
      map.set(key, entry);
    });

    // Include customers with no orders (neverOrdered)
    customers.forEach(c => {
      const key = makeKey(c.address.neighborhood, c.address.street, c.address.buildingInfo);
      if(!map.has(key)) {
        map.set(key, { lastOrderDate: undefined, orderCount:0, totalAmount:0, customers: new Set([c.id]), n: c.address.neighborhood, s: c.address.street, b: c.address.buildingInfo });
      } else {
        // ensure the customer is part of set
        const existing = map.get(key)!;
        existing.customers.add(c.id);
      }
    });

    const todayStr = new Date().toISOString().split('T')[0];
    const today = new Date(todayStr + 'T00:00:00');

    const computeSegment = (days?: number): CustomerSegment => {
      if(days === undefined) return 'neverOrdered';
      if(days <= settings.activeDays) return 'active';
      if(days <= settings.slippingDays) return 'slipping';
      if(days <= settings.churnRiskDays) return 'churnRisk';
      return 'lost';
    };

    const list: AddressStat[] = [];
    map.forEach((v, key) => {
      let days: number | undefined = undefined;
      if(v.lastOrderDate) {
        const diffMs = today.getTime() - new Date(v.lastOrderDate + 'T00:00:00').getTime();
        days = Math.floor(diffMs / 86400000);
      }
      const segment = computeSegment(days);
      list.push({
        addressKey: key,
        neighborhood: v.n,
        street: v.s,
        buildingInfo: v.b,
        customers: Array.from(v.customers),
        lastOrderDate: v.lastOrderDate,
        orderCount: v.orderCount,
        totalAmount: v.totalAmount,
        segment,
        daysSinceLast: days,
      });
    });

    return list;
  }, [orders, customers, settings.activeDays, settings.slippingDays, settings.churnRiskDays]);

  // Passive addresses (churnRisk + lost + optional neverOrdered)
  const passiveAddresses = useMemo(() => addressStats.filter(a => (
    a.segment === 'slipping' || a.segment === 'churnRisk' || a.segment === 'lost' || (settings.includeNeverOrdered && a.segment === 'neverOrdered')
  )), [addressStats, settings.includeNeverOrdered]);

  // Newly passive detection
  const [previousPassive, setPreviousPassive] = useState<string[]>(() => {
    try { const raw = localStorage.getItem(LS_PREV_PASSIVE); if(raw) return JSON.parse(raw); } catch {}
    return [];
  });

  const passiveKeys = useMemo(() => passiveAddresses.map(a => a.addressKey).sort(), [passiveAddresses]);
  const newlyPassiveKeys = useMemo(()=> passiveKeys.filter(k => !previousPassive.includes(k)), [passiveKeys, previousPassive]);

  useEffect(()=>{
    // Persist current passive keys
    localStorage.setItem(LS_PREV_PASSIVE, JSON.stringify(passiveKeys));
    setPreviousPassive(passiveKeys); // once persisted treat as seen
  }, [passiveKeys]);

  const clearNewlyPassive = useCallback(() => {
    setPreviousPassive(passiveKeys);
  }, [passiveKeys]);

  // Campaign Management
  const [campaigns, setCampaigns] = useState<Campaign[]>(() => {
    try {
      const raw = localStorage.getItem(LS_CAMPAIGNS_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        // Varsayılan kampanyaları birleştir
        const defaultIds = defaultCampaigns.map(c => c.id);
        const userCampaigns = saved.filter((c: Campaign) => !defaultIds.includes(c.id));
        return [...defaultCampaigns, ...userCampaigns];
      }
    } catch {}
    return defaultCampaigns;
  });

  useEffect(() => {
    localStorage.setItem(LS_CAMPAIGNS_KEY, JSON.stringify(campaigns));
  }, [campaigns]);

  const addCampaign = useCallback((campaign: Omit<Campaign, 'id' | 'createdAt'>) => {
    const newCampaign: Campaign = {
      ...campaign,
      id: `campaign-${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    setCampaigns(prev => [...prev, newCampaign]);
  }, []);

  const updateCampaign = useCallback((id: string, updates: Partial<Campaign>) => {
    setCampaigns(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  }, []);

  const deleteCampaign = useCallback((id: string) => {
    // Varsayılan kampanyaları silmeye izin verme
    if (id.startsWith('default-')) return;
    setCampaigns(prev => prev.filter(c => c.id !== id));
  }, []);

  // Discount Code Management
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>(() => {
    try {
      const raw = localStorage.getItem(LS_DISCOUNT_CODES_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return [];
  });

  useEffect(() => {
    localStorage.setItem(LS_DISCOUNT_CODES_KEY, JSON.stringify(discountCodes));
  }, [discountCodes]);

  const addDiscountCode = useCallback((code: Omit<DiscountCode, 'id' | 'createdAt' | 'usageCount'>) => {
    const newCode: DiscountCode = {
      ...code,
      id: `code-${Date.now()}`,
      usageCount: 0,
      createdAt: new Date().toISOString()
    };
    setDiscountCodes(prev => [...prev, newCode]);
  }, []);

  const updateDiscountCode = useCallback((id: string, updates: Partial<DiscountCode>) => {
    setDiscountCodes(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  }, []);

  const deleteDiscountCode = useCallback((id: string) => {
    setDiscountCodes(prev => prev.filter(c => c.id !== id));
  }, []);

  const validateDiscountCode = useCallback((code: string, orderAmount: number, userId?: string) => {
    const discountCode = discountCodes.find(c => c.code.toUpperCase() === code.toUpperCase());
    
    if (!discountCode) {
      return { valid: false, message: 'Geçersiz indirim kodu' };
    }

    if (!discountCode.isActive) {
      return { valid: false, message: 'Bu kod artık aktif değil' };
    }

    const today = new Date().toISOString().split('T')[0];
    if (today < discountCode.startDate || today > discountCode.endDate) {
      return { valid: false, message: 'Bu kod geçerlilik tarihlerinde değil' };
    }

    if (discountCode.minOrderAmount && orderAmount < discountCode.minOrderAmount) {
      return { valid: false, message: `Minimum sipariş tutarı: ${discountCode.minOrderAmount}₺` };
    }

    if (discountCode.usageLimit && discountCode.usageCount >= discountCode.usageLimit) {
      return { valid: false, message: 'Bu kod kullanım limitine ulaştı' };
    }

    // İndirim hesapla
    let discount = 0;
    if (discountCode.discountType === 'percentage') {
      discount = (orderAmount * discountCode.discountValue) / 100;
    } else {
      discount = discountCode.discountValue;
    }

    return { valid: true, discount, message: 'Kod uygulandı!' };
  }, [discountCodes]);

  const useDiscountCode = useCallback((code: string) => {
    setDiscountCodes(prev => prev.map(c =>
      c.code.toUpperCase() === code.toUpperCase()
        ? { ...c, usageCount: c.usageCount + 1 }
        : c
    ));
  }, []);

  // Context value memoization - gereksiz re-render'ları önler
  const contextValue = useMemo(() => ({ 
    settings, 
    updateSettings, 
    addressStats, 
    passiveAddresses, 
    newlyPassiveKeys, 
    clearNewlyPassive,
    campaigns,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    discountCodes,
    addDiscountCode,
    updateDiscountCode,
    deleteDiscountCode,
    validateDiscountCode,
    useDiscountCode
  }), [
    settings, 
    updateSettings, 
    addressStats, 
    passiveAddresses, 
    newlyPassiveKeys, 
    clearNewlyPassive,
    campaigns,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    discountCodes,
    addDiscountCode,
    updateDiscountCode,
    deleteDiscountCode,
    validateDiscountCode,
    useDiscountCode
  ]);

  return (
    <MarketingContext.Provider value={contextValue}>
      {children}
    </MarketingContext.Provider>
  );
};

export default MarketingContext;
